import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const FUNCTION_URL = 'https://stellular-clafoutis-10452c.netlify.app/.netlify/functions/migracion-chat';

const WELCOME = { role: 'assistant', content: 'Hola, soy tu asistente de regularización migratoria en Brasil. Tienes 15 preguntas gratis en este asesor. ¿En qué trámite te puedo ayudar hoy?' };

function formatMessage(text) {
  let safe = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  safe = safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  safe = safe.replace(/^##\s?(.+)$/gm, '<strong style="display:block;margin-top:8px;font-size:15px;">$1</strong>');
  safe = safe.replace(/^#\s?(.+)$/gm, '<strong style="display:block;margin-top:8px;font-size:16px;">$1</strong>');

  safe = safe.replace(
    /((https?:\/\/|www\.)[^\s<]+)/g,
    (match) => {
      const cleanMatch = match.replace(/[.,;:!?)\]]+$/, '');
      const trailing = match.slice(cleanMatch.length);
      const href = cleanMatch.startsWith('http') ? cleanMatch : `https://${cleanMatch}`;
      return `<a href="${href}" rel="noopener noreferrer" style="color:#FF8A00;text-decoration:underline;">${cleanMatch}</a>${trailing}`;
    }
  );

  safe = safe.replace(/\n/g, '<br/>');

  return safe;
}

export default function MigracionChat({ onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);
  const sendingRef = useRef(false);
  const [generatingChecklist, setGeneratingChecklist] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const chatId = user?.uid;
    if (!chatId) { setLoadingHistory(false); return; }

    const loadHistory = async () => {
      try {
        const res = await fetch(FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId, historyOnly: true }),
        });
        const data = await res.json();
        if (data.history && data.history.length > 0) {
          setMessages([WELCOME, ...data.history]);
        }
      } catch (err) {
        // Si falla, se queda con el mensaje de bienvenida
      } finally {
        setLoadingHistory(false);
      }
    };

    loadHistory();
  }, [user?.uid]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading || sendingRef.current) return;
    sendingRef.current = true;
    setLoading(true);

    const chatId = user?.uid;
    if (!chatId) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No pudimos identificar tu sesión. Por favor cierra sesión y vuelve a entrar.' }]);
      setLoading(false);
      sendingRef.current = false;
      return;
    }

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');

    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, message: text }),
      });
      const data = await res.json();

      if (data.limitReached) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message, isPaywall: true }]);
      } else if (data.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Hubo un problema al procesar tu mensaje. Intenta de nuevo.' }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No se pudo conectar. Revisa tu internet e intenta de nuevo.' }]);
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  const downloadChecklist = async () => {
    const chatId = user?.uid;
    if (!chatId || generatingChecklist) return;
    setGeneratingChecklist(true);
    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, generateChecklist: true }),
      });
      const data = await res.json();
      if (data.checklistBlocked) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message, isPaywall: true }]);
        return;
      }
      if (data.checklist) {
        const blob = new Blob([data.checklist], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'checklist.txt';
        a.click();
        URL.revokeObjectURL(url);
      } else if (data.message) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No se pudo generar el checklist. Intenta de nuevo.' }]);
    } finally {
      setGeneratingChecklist(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '16px', borderBottom: '1px solid var(--navy-border)', background: 'var(--bg)'
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--white)', fontSize: 20, cursor: 'pointer' }}>←</button>
        <div>
          <div style={{ color: 'var(--white)', fontWeight: 800, fontSize: 16 }}>Asistente Migratorio</div>
          <div style={{ color: 'var(--muted)', fontSize: 12 }}>Regularización en Brasil</div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loadingHistory && (
          <div style={{ alignSelf: 'center', color: 'var(--muted)', fontSize: 13 }}>Cargando conversación...</div>
        )}
        {!loadingHistory && messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div
              style={{
                maxWidth: '85%',
                background: m.role === 'user' ? 'var(--orange)' : 'var(--navy-card)',
                color: 'var(--white)',
                padding: '10px 14px',
                borderRadius: 14,
                fontSize: 14,
                lineHeight: 1.5,
                border: m.isPaywall ? '1px solid var(--orange)' : 'none',
              }}
              dangerouslySetInnerHTML={{ __html: formatMessage(m.content) }}
            />
            {m.isPaywall && (
              <button
                onClick={() => window.open('https://api.whatsapp.com/send?phone=5541920004775&text=' + encodeURIComponent('Hola, quiero suscribirme al plan Pro de Asesores (R$19,90/mes)'), '_blank')}
                style={{ background: 'var(--orange)', color: 'var(--white)', border: 'none', borderRadius: 20, padding: '8px 16px', fontSize: 13, marginTop: 6, cursor: 'pointer' }}
              >
                Quiero ser Pro
              </button>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: 'flex-start', color: 'var(--muted)', fontSize: 13, padding: '0 14px' }}>
            Escribiendo...
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      <div style={{
        display: 'flex', gap: 8, padding: '12px 16px',
        borderTop: '1px solid var(--navy-border)', background: 'var(--bg)'
      }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe tu pregunta..."
          style={{
            flex: 1, padding: '12px 14px', borderRadius: 20,
            border: '1px solid var(--navy-border)', background: 'var(--navy-card)',
            color: 'var(--white)', fontSize: 14, outline: 'none',
          }}
        />
        <button
          onClick={downloadChecklist}
          disabled={generatingChecklist}
          title="Descargar checklist"
          style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', color: 'var(--white)', borderRadius: '50%', width: 44, height: 44, fontSize: 16, cursor: 'pointer', opacity: generatingChecklist ? 0.5 : 1 }}
        >
          📋
        </button>
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          style={{
            background: 'var(--orange)', color: 'var(--white)', border: 'none',
            borderRadius: '50%', width: 44, height: 44,
            fontSize: 18, cursor: 'pointer',
            opacity: (loading || !input.trim()) ? 0.5 : 1,
          }}
        >
          ➤
        </button>
      </div>
    </div>
  );
}
